import { useMutation, useQuery } from 'react-query';
import { dateToString, getLocalDate, getLocalDateStr, listToStringList, removeDuplicates, stringToDate, toTitleCase } from 'common/utils';
import * as procedureService from 'services/procedureService';
import { EInsuranceStatus } from 'enum/EInsuranceStatus';

const getProcedureById = async (userId: number, procedureId: number) => {
  const procedure = await procedureService.getProcedureById(userId, procedureId);
  const images = await procedureService.getProcedureImages(userId, procedureId);

  procedure.insurance_status = toTitleCase(procedure.insurance_status);

  const newImages = (images ?? []).map((item: any) => ({
    id: item.id,
    uri: item.urls.href_original,
    image_name: item.image_name,
    procedure: item.procedure,
  }));

  return { ...procedure, images: newImages };
}

const createProcedure = async ({ userId, data, approve = false }: {
  userId: number,
  data: any,
  approve?: boolean,
}) => {
  try {
    if (!approve) {
      const newData = { ...data };

      for (let key in newData) {

        // Procedure lists
        if (['approach', 'indication', 'location', '_procedure', '_vendors', 'position'].includes(key)) {
          newData[key] = listToStringList(newData[key]);
        }
      }

      // Procedure dates
      newData.date_of_procedure = dateToString(getLocalDate(newData.date_of_procedure));
      newData.date = newData.date_of_procedure;

      newData.gender = newData.gender.toUpperCase();

      if (!newData.date_of_birth) {
        delete newData.date_of_birth;
      } else {
        newData.date_of_birth = dateToString(
          newData.date_of_birth.toDate()
        );
      }

      if (!newData.cpt_modifier_id) {
        delete newData.cpt_modifier;
      }

      if (!newData.insurance_status) {
        newData.insurance_status = EInsuranceStatus.PENDING;
      }

      for (let cp of data.case_procedures) {
        delete cp.id;
        for (let key in cp) {
          if (['approach', 'indication', 'location', '_procedure', '_vendors', 'position'].includes(key)) {
            cp[key] = listToStringList(cp[key]);
          }
        }
        cp.date_of_procedure = getLocalDateStr(cp.date_of_procedure);
        cp.date = cp.date_of_procedure;
      }
      newData.cpt_modifier_id = newData?.cpt_modifier ? newData.cpt_modifier.id : null;
      newData.notes = newData.notes.filter((item: string) => item.trim() != '');

      if (newData?.notes?.length == 0) {
        delete newData.notes;
      }
      return await procedureService.createProcedure(userId, newData);
    }

    return await procedureService.createProcedure(userId, data);
  } catch (error) {
    console.log('error creating procedure', error);
  }
}

const updateProcedure = async ({ userId, procedureId, data }: {
  userId: number;
  procedureId: number;
  data: any;
}) => {
  try {
    const newData = { ...data };

    for (let key in newData) {

      // Procedure lists at the root level
      if (['approach', 'indication', 'location', '_procedure', '_vendors', 'position'].includes(key)) {
        newData[key] = listToStringList(newData[key]);
      }

      if (newData[key] == null) {
        newData[key] = '';
      }
    }

    if (newData.gender) {
      newData.gender = newData.gender.toUpperCase();
    }

    // Procedure dates
    if (newData.date_of_procedure) {
      newData.date_of_procedure = dateToString(getLocalDate(newData.date_of_procedure));
      newData.date = newData.date_of_procedure;
    }

    // CPT Modifier
    if (newData.cpt_modifier) {
      newData.cpt_modifier_id = newData?.cpt_modifier ? newData.cpt_modifier.id : null;
    }

    // Notes
    if (newData.notes) {
      newData.notes = newData?.notes?.filter((item: string) => item.trim() != '') || [];
    }

    if (!newData.date_of_birth) {
      delete newData.date_of_birth;
    } else {
      newData.date_of_birth = dateToString(
        newData.date_of_birth.toDate()
      );
    }

    if (!newData.cpt_modifier_id) {
      delete newData.cpt_modifier;
    }

    delete newData.hospital;

    for (let cp of newData.case_procedures) {
      for (let key in cp) {
        if (['approach', 'indication', 'location', '_procedure', '_vendors', 'position'].includes(key)) {
          cp[key] = listToStringList(cp[key]);
        }
      }

      try {
        cp.date_of_procedure = getLocalDateStr(cp.date_of_procedure);
      } catch (error) { }

      cp.date = cp.date_of_procedure;
      delete cp.cpt_modifier;
      delete cp.case_procedure_cpt;

    }


    return await procedureService.updateProcedure(userId, procedureId, newData);
  } catch (error) {
    console.log('error updating procedure', error);
  }
}

const deleteCaseProcedure = async ({ userId, procedureId, caseProcedureId }: {
  userId: number,
  procedureId: number,
  caseProcedureId: number,
}) => {
  return await procedureService.deleteCaseProcedureById(userId, procedureId, caseProcedureId);
}

const updateProcedureImages = async ({
  userId,
  procedureId,
  images,
}: {
  userId: number;
  procedureId: number;
  images: { uri: string; image_name: string }[];
}) => {
  const formData = new FormData();

  for (const image of images) {
    const response = await fetch(image.uri);
    const blob = await response.blob();

    const file = new File([blob], image.image_name, {
      type: blob.type || 'image/jpeg',
    });

    formData.append('images', file);
  }

  return procedureService.updateCaseImages(userId, procedureId, formData);
};


const deleteCaseImage = async ({
  userId,
  procedureId,
  imageId
}: {
  userId: number,
  procedureId: number,
  imageId: number
}) => {
  return await procedureService.deleteCaseImage(userId, procedureId, imageId);
}

const updateCaseImage = async ({
  userId,
  procedureId,
  image
}: {
  userId: number,
  procedureId: number,
  image: any
}) => {
  return await procedureService.updateProcedureImage(userId, procedureId, image);
}

const deleteProcedure = async ({ userId, procedureId }: {
  userId: number,
  procedureId: number,
}) => {
  return await procedureService.deleteProcedures(userId, [procedureId]);
}

const deleteProcedures = async ({ userId, procedureIds }: {
  userId: number,
  procedureIds: number[],
}) => {
  return await procedureService.deleteProcedures(userId, procedureIds);
}

const getProcedureInvitations = async () => {
  return await procedureService.getProcedureInvitations();
}

const approveProcedureInvitation = async (invitationId: number) => {
  return await procedureService.approveProcedureInvitation(invitationId);
}

const rejectProcedureInvitation = async (invitationId: number) => {
  return await procedureService.rejectProcedureInvitation(invitationId);
}

const updateProcedureNotes = async ({ userId, procedureId, notes }: {
  userId: number,
  procedureId: number,
  notes: any[],
}) => {
  return await procedureService.updateNotes(userId, procedureId, notes);
}

// ******************************************************************
// ******************************************************************
// ******************************************************************
// ******************************************************************
// ******************************************************************
// ******************************************************************
// ******************************************************************
// ******************************************************************
// ******************************************************************
// ******************************************************************

export const useGetProcedureById = (procedureId: number, userId: number, options = {}) => {
  return useQuery({
    queryKey: ['procedure', userId, procedureId],
    queryFn: () => getProcedureById(userId, procedureId),
    keepPreviousData: false,
    ...options,
  });
}

export const useUpdateProcedure = (onSuccess: any, onError: any, options = {}) => {
  return useMutation(updateProcedure, {
    onSuccess,
    onError,
    ...options,
  });
}

export const useCreateProcedure = (onSuccess: any, onError: any) => {
  return useMutation(createProcedure, {
    onSuccess,
    onError
  });
}

export const useDeleteCaseProcedure = (onSuccess: any, onError: any, options = {}) => {
  return useMutation(deleteCaseProcedure, {
    onSuccess,
    onError,
    ...options,
  });
}

export const useUpdateProcedureImages = (onSuccess: any, onError: any) => {
  return useMutation(updateProcedureImages, {
    onSuccess,
    onError
  });
}

export const useDeleteCaseImage = (onSuccess: any, onError: any, options = {}) => {
  return useMutation(deleteCaseImage, {
    onSuccess,
    onError,
    ...options,
  });
}

export const useUpdateProcedureImage = (onSuccess: any, onError: any, options = {}) => {
  return useMutation(updateCaseImage, {
    onSuccess,
    onError,
    ...options,
  });
}

export const useDeleteProcedure = (onSuccess: any, onError: any, options = {}) => {
  return useMutation(deleteProcedure, {
    onSuccess,
    onError,
    ...options,
  });
}

export const useDeleteProcedures = (onSuccess: any, onError: any, options = {}) => {
  return useMutation(deleteProcedures, {
    onSuccess,
    onError,
    ...options,
  });
}

export const useGetProcedureInvitations = (options = {}) => {
  return useQuery({
    queryKey: ['procedureInvitations'],
    queryFn: getProcedureInvitations,
    ...options,
  });
}

export const useApproveProcedureInvitation = (onSuccess: any, onError: any, options = {}) => {
  return useMutation(approveProcedureInvitation, {
    onSuccess,
    onError,
    ...options,
  });
}

export const useRejectProcedureInvitation = (onSuccess: any, onError: any, options = {}) => {
  return useMutation(rejectProcedureInvitation, {
    onSuccess,
    onError,
    ...options,
  });
}

export const useUpdateProcedureNotes = (onSuccess: any, onError: any, options = {}) => {
  return useMutation(updateProcedureNotes, {
    onSuccess,
    onError,
    ...options,
  });
}
